import React from 'react'
import styled, {keyframes} from 'styled-components'
import {AkCheckIcon} from '@fuegokit/fuegoicons-react'
import Select, {SelectItemProps} from '@fuegokit/react/components/Select'
import themeGet from '@fuegokit/react/utils/theme-get'

// animations
const slideUpAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`
const slideRightAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(-2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`
const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`
const slideLeftAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`
// styles
export const StyledSelect = styled(Select)`
  button {
    all: unset;
  }
`
export const StyledSelectTrigger = styled(Select.Trigger)`
  // reset
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  user-select: none;
  display: flex;
  align-items: center;
  color: inherit;
  margin-left: ${themeGet('space.4')};
  font-weight: normal;
  font-size: ${themeGet('fontSizes.3')};
  text-decoration: none;
  opacity: 0.85;
  font-weight: ${themeGet('fontWeights.semibold')};
  padding: ${themeGet('space.2')} 12px;
  border-radius: ${themeGet('radii.1')};
  border: 1px solid ${themeGet('colors.border.input')};
  &:hover,
  &:focus {
    opacity: 1;
    text-decoration: none;
    color: ${themeGet('colors.link.default')};
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
  }
  &:active {
    opacity: 1;
    text-decoration: none;
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
    border-color: ${themeGet('colors.border.focused')};
  }
  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  svg {
    transition: transform 0.15s ease;
  }
  &[data-state='open'] {
    --icon-rotation: 180deg;
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
    svg {
      transform: rotate(var(--icon-rotation));
    }
  }
`
export const StyledSelectContent = styled(Select.Content)`
  min-width: 184px;
  max-width: 800px;

  position: absolute;
  overflow: auto;
  display: flex;
  flex-direction: column;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: 1px solid ${themeGet('colors.border.default')};
  border-radius: ${themeGet('radii.2')};
  padding: ${themeGet('space.2')} 0;
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
  &[data-side='top'] {
    animation-name: ${slideDownAndFade};
  }
  &[data-side='right'] {
    animation-name: ${slideLeftAndFade};
  }
  &[data-side='bottom'] {
    animation-name: ${slideUpAndFade};
  }
  &[data-side='left'] {
    animation-name: ${slideRightAndFade};
  }
`
export const StyledSelectViewport = styled(Select.Viewport)`
  padding: ${themeGet('space.3')};
`
export const StyledSelectScrollButton = styled(Select.ScrollUpButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${themeGet('space.5')};
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  color: ${themeGet('colors.text.subtle')};
  cursor: default;
`
export const StyledSelectLabel = styled(Select.Label)`
  padding: 0;
  font-size: ${themeGet('fontSizes.3')};
  line-height: ${themeGet('lineHeights.relaxed')};
  color: ${themeGet('colors.text.subtle')};
`
export const StyledSelectSeparator = styled(Select.Separator)`
  height: 1px;
  background-color: ${themeGet('colors.border.default')};
  margin: ${themeGet('space.2')} ${themeGet('space.1')};
`
export const StyledSelectItem = React.forwardRef(
  ({children, UNSAFE_className, ...props}: SelectItemProps, forwardedRef) => {
    return (
      <StyledSelectItemComponent {...props} ref={forwardedRef}>
        <Select.ItemText>{children}</Select.ItemText>
        <StyledItemIndicator>
          <AkCheckIcon />
        </StyledItemIndicator>
      </StyledSelectItemComponent>
    )
  }
)
export const StyledSelectItemComponent = styled(Select.Item)`
  font-size: ${themeGet('fontSizes.3')};
  line-height: 1;
  min-height: 40px;
  color: ${themeGet('colors.text.default')};
  display: flex;
  align-items: center;

  height: ${themeGet('space.5')};
  padding: 0 ${themeGet('space.2')};
  position: relative;
  padding-left: ${themeGet('space.3')};
  user-select: none;
  outline: none;
  text-align: left;

  margin: 0;
  text-decoration: none;

  &[data-highlighted] {
    outline: none;
    color: ${themeGet('colors.text.default')};
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
    cursor: pointer;
  }
  &[data-disabled] {
    color: ${themeGet('colors.text.disabled')};
  }
  &:hover {
    text-decoration: none;
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
  }
  &:active {
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
  }
  &:visited {
    color: ${themeGet('colors.text.selected')};
  }
`
export const StyledItemIndicator = styled(Select.ItemIndicator)`
  position: absolute;
  left: -16px;
  width: ${themeGet('space.6')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${themeGet('colors.icon.subtle')};
`
StyledSelectItem.displayName = 'StyledSelectItem'

// temp, radix demos
export const StyledRadixSelectTrigger = styled(Select.Trigger)`
  display: flex;
  align-items: center;
  gap: ${themeGet('space.3')};
  border: 1px solid ${themeGet('colors.border.default')};
  border-radius: ${themeGet('radii.1')};
  background-color: transparent;
  height: ${themeGet('space.6')};
  padding: 5px 15px;
  font-family: ${themeGet('fontFamilies.normal')};
  font-size: ${themeGet('fontSizes.3')};
  line-height: 1;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
`
export const StyledRadixSelectContent = styled(Select.Content)`
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: 1px solid ${themeGet('colors.border.default')};
  border-radius: ${themeGet('radii.2')};
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  position: relative;
  &:focus-within {
    border-color: ${themeGet('colors.border.focused')};
  }
  min-width: var(--radix-select-trigger-width);
  max-height: var(--radix-select-content-available-height);
`
export const StyledRadixSelectContentWithPadding = styled(Select.Content)`
  padding: ${themeGet('space.3')};
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: 1px solid ${themeGet('colors.border.default')};
  border-radius: ${themeGet('radii.2')};
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  position: relative;
  &:focus-within {
    border-color: ${themeGet('colors.border.focused')};
  }
  min-width: var(--radix-select-trigger-width);
  max-height: var(--radix-select-content-available-height);
`
export const StyledRadixSelectViewport = styled(Select.Viewport)`
  padding: ${themeGet('space.3')};
`
export const StyledRadixSelectGroup = styled(Select.Group)``
export const StyledRadixSelectItem = styled(Select.Item)`
  display: flex;
  align-items: center;
  line-height: 1;
  cursor: default;
  user-select: none;
  white-space: nowrap;
  height: ${themeGet('space.4')};
  padding: 0 ${themeGet('space.6')};
  font-family: ${themeGet('fontFamilies.normal')};
  font-size: ${themeGet('fontSizes.3')};
  color: ${themeGet('colors.text.default')};
  border-radius: ${themeGet('radii.1')};
  //
  position: relative;
  outline: none;

  &[data-highlighted] {
    background-color: ${themeGet('colors.background.neutral.bold.pressed')};
    color: ${themeGet('colors.text.inverse')};
  }
  &[data-disabled] {
    color: ${themeGet('colors.text.disabled')};
  }
  &[dir='rtl'] {
    font-size: 16px;
    font-weight: bold;
  }
`
export const StyledRadixSelectItemInGroup = styled(Select.Item)`
  padding: ${themeGet('space.7')};
  display: flex;
  align-items: center;
  line-height: 1;
  cursor: default;
  user-select: none;
  white-space: nowrap;
  height: ${themeGet('space.4')};
  /* padding: 0 ${themeGet('space.6')}; */
  font-family: ${themeGet('fontFamilies.normal')};
  font-size: ${themeGet('fontSizes.3')};
  color: ${themeGet('colors.text.default')};
  border-radius: ${themeGet('radii.1')};
  //
  position: relative;
  outline: none;

  &[data-highlighted] {
    background-color: ${themeGet('colors.background.neutral.bold.pressed')};
    color: ${themeGet('colors.text.inverse')};
  }
  &[data-disabled] {
    color: ${themeGet('colors.text.disabled')};
  }
  &[dir='rtl'] {
    font-size: 16px;
    font-weight: bold;
  }
`
export const StyledRadixSelectLabel = styled(Select.Label)`
  color: ${themeGet('colors.text.subtle')};
  font-weight: ${themeGet('fontWeights.semibold')};
`
export const StyledRadixItemIndicator = styled(Select.ItemIndicator)`
  position: absolute;
  left: ${themeGet('space.2')};
  top: ${themeGet('space.2')};
  & svg {
    display: block;
  }
  &[dir='rtl'] {
    left: auto;
    right: ${themeGet('space.2')};
  }
`
export const StyledRadixScrollUpButton = styled(Select.ScrollUpButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: ${themeGet('space.4')};
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  color: ${themeGet('colors.text.default')};
  cursor: default;
  user-select: none;

  //
  border-bottom: '1px solid ${themeGet('colors.border.default')}';
  border-top-left-radius: ${themeGet('radii.3')};
  border-top-right-radius: ${themeGet('radii.3')};
`
export const StyledRadixScrollDownButton = styled(Select.ScrollDownButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: ${themeGet('space.4')};
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  color: ${themeGet('colors.text.default')};
  cursor: default;
  user-select: none;

  //
  border-top: '1px solid ${themeGet('colors.border.default')}';
  border-bottom-left-radius: ${themeGet('radii.3')};
  border-bottom-right-radius: ${themeGet('radii.3')};
`
export const StyledRadixSeparator = styled(Select.Separator)`
  height: 1px;
  margin: 4px -4px;
  background-color: ${themeGet('colors.border.subtle')};
`
